import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Icon } from './icons';

export type ConfirmOptions = {
  title: string;
  /** Secondary line under the title. Keep it to the "what will actually happen" part. */
  sub?: string;
  /** Body copy. Line breaks are rendered as separate paragraphs. */
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' paints the confirm button red and moves the initial focus to Cancel. */
  tone?: 'default' | 'danger';
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmFn | null>(null);

type Pending = { opts: ConfirmOptions; resolve: (ok: boolean) => void };

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setPending({ opts, resolve })),
    [],
  );

  const settle = useCallback((ok: boolean) => {
    setPending((p) => {
      p?.resolve(ok);
      return null;
    });
  }, []);

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {pending && <ConfirmDialog opts={pending.opts} onSettle={settle} />}
    </Ctx.Provider>
  );
}

function ConfirmDialog({
  opts,
  onSettle,
}: {
  opts: ConfirmOptions;
  onSettle: (ok: boolean) => void;
}) {
  const danger = opts.tone === 'danger';
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Destructive actions focus Cancel, so a stray Enter never fires them.
  useEffect(() => {
    (danger ? cancelRef : confirmRef).current?.focus();
  }, [danger]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSettle(false);
      } else if (e.key === 'Enter' && !danger) {
        e.preventDefault();
        onSettle(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [danger, onSettle]);

  const paragraphs = (opts.body ?? '').split('\n').filter((line) => line.trim());

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. This dialog has its own Escape and Enter handling below, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onSettle(false);
      }}
    >
      <div
        className="modal-card"
        role="alertdialog"
        aria-modal="true"
        aria-label={opts.title}
        style={{ maxWidth: 440 }}
      >
        <div className="modal-head">
          <div>
            <div className="modal-title">{opts.title}</div>
            {opts.sub && <div className="modal-sub">{opts.sub}</div>}
          </div>
          <button type="button" className="icon-btn" onClick={() => onSettle(false)} title="Cerrar">
            <Icon.X size={14} />
          </button>
        </div>

        {paragraphs.length > 0 && (
          <div className="modal-body">
            <div className="confirm-copy">
              {danger && (
                <span className="confirm-icon">
                  <Icon.Alert size={15} stroke="#fca5a5" />
                </span>
              )}
              <div>
                {paragraphs.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="modal-foot">
          <button
            ref={cancelRef}
            type="button"
            className="cta-secondary"
            onClick={() => onSettle(false)}
          >
            {opts.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={danger ? 'cta-danger' : 'cta'}
            onClick={() => onSettle(true)}
          >
            {opts.confirmLabel ?? 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useConfirm must be inside ConfirmProvider');
  return ctx;
}
